import { frameId } from "../assets/manifest.ts";
import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type LayerDocument, type MapDocument, type PropInstance, type TerrainLayer, type TileRef } from "../document/types.ts";
import { propBox, type IssueBox } from "./issue-entries.ts";
import { layerIsEmpty } from "./layer-empty.ts";
import { MissingFrameError, type FrameMask, type FrameMaskOf } from "./occlusion.ts";
import type { PresenceArea } from "./presence-area.ts";

/**
 * Highlight layer (ADR-070's amendment of 2026-10-04): where a layer's content is on its map and, for a tile or
 * terrain layer, how much of it a player still sees. Pure functions over the document and the cells the
 * renderer stamps; nothing here is saved, compiled or sent.
 *
 * A tile or terrain layer holds the cells that draw a tile. With the frames' solid pixels given (`maskOf`, which
 * a host binds from its atlas) the answer is measured: a cell is `covered` when one tile or one prop drawn over
 * it is solid over the whole cell, and `showing` otherwise, so a see-through tile, a transition and the clear
 * corners of a prop's frame cover nothing. Without them it is an estimate: `covered` when a terrain layer drawn
 * over the cell paints all four of its corners, `under` when a tile layer drawn over it holds any tile there or
 * a prop's frame box holds the whole cell. A prop layer holds the cells its props' frames cover, a marker layer
 * its markers' cells, a collision or zone layer its painted quarter cells; none of those is judged.
 */

/**
 * What the rules read besides the document: the cells a tile or terrain layer draws (`EMPTY_TILE` where it
 * draws nothing; null for a layer without cells), a prop frame's size in pixels, the tile size, the world's
 * tileset ids in the order packed tiles index them, the number of ground layers (ADR-047) and, when the host
 * has them, the solid pixels of any frame by frame id, which turn the estimate into a measurement.
 */
export interface HighlightSources {
  readonly cells: (layerId: string) => Uint32Array | null;
  readonly frameSize: (frame: TileRef) => { readonly w: number; readonly h: number } | undefined;
  readonly tileSize: number;
  readonly tilesets: readonly string[];
  readonly playersAbove: number;
  readonly maskOf?: FrameMaskOf | undefined;
}

/**
 * A highlight's counts. `total` counts cells for a tile, terrain, collision or zone layer and things for a prop
 * or marker layer. For a judged layer `showing`, `covered` and `under` add up to `total`; for any other,
 * `showing` is `total`. `under` is 0 for a measured highlight.
 */
export interface HighlightCounts {
  readonly total: number;
  readonly showing: number;
  readonly covered: number;
  readonly under: number;
}

/**
 * One layer's highlight: three bitmaps over the whole map on a grid of `cellPixels` world pixels (a tile for
 * tiles, terrain, props and markers; a quarter tile for collision and zones), the cells that show, the cells
 * that are covered and the cells estimated to be under a tile or prop; whether the layer's kind is judged at
 * all, and whether the judgement was measured from the frames' pixels or is an estimate; whether the layer is
 * empty by the one rule (`layerIsEmpty`: a terrain layer whose painted corners resolve to no tile holds no cell
 * and is not empty); the counts; and the content's bounding box in world pixels, null when nothing is drawn.
 */
export interface LayerHighlight {
  readonly layerId: string;
  readonly kind: LayerDocument["kind"];
  readonly empty: boolean;
  readonly judged: boolean;
  readonly measured: boolean;
  readonly cellPixels: number;
  readonly showing: PresenceArea;
  readonly covered: PresenceArea;
  readonly under: PresenceArea;
  readonly counts: HighlightCounts;
  readonly bounds: IssueBox | null;
}

const SHOWING = 1;
const COVERED = 2;
const UNDER = 3;

/**
 * Whether one layer draws over another in the same cell, by the renderer's order (ADR-047): ground layers stack
 * by index; every layer of the Players band draws over every ground layer; in the band terrain lies flat at the
 * band's floor, so every other kind draws over it whatever their order, and two flat layers or two sorting
 * layers keep their order.
 *
 * @param layers The map's layers, bottom first.
 * @param playersAbove The number of ground layers.
 * @param below The index of the layer that may be drawn over.
 * @param above The index of the layer that may draw over it.
 * @returns True when the layer at `above` draws over the layer at `below`.
 */
export function drawsOver(layers: readonly LayerDocument[], playersAbove: number, below: number, above: number): boolean {
  if (below === above) {
    return false;
  }
  const belowInBand = below >= playersAbove;
  const aboveInBand = above >= playersAbove;
  if (belowInBand !== aboveInBand) {
    return aboveInBand;
  }
  if (!belowInBand) {
    return above > below;
  }
  const belowFlat = layers[below]?.kind === "terrain";
  const aboveFlat = layers[above]?.kind === "terrain";
  return belowFlat === aboveFlat ? above > below : belowFlat;
}

/**
 * Marks the cells a terrain layer is estimated to cover: those it draws with all four of the cell's corners
 * painted.
 *
 * @param states The states of the highlighted layer's cells, changed in place.
 * @param layer The terrain layer drawn over it.
 * @param drawn The cells that layer draws.
 * @param width The map's width in cells.
 * @param height The map's height in cells.
 */
function coverByCorners(states: Uint8Array, layer: TerrainLayer, drawn: Uint32Array, width: number, height: number): void {
  const corners = layer.corners;
  const stride = width + 1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const at = y * width + x;
      if (states[at] === 0 || states[at] === COVERED || drawn[at] === EMPTY_TILE) {
        continue;
      }
      const corner = y * stride + x;
      if (corners[corner] !== 0 && corners[corner + 1] !== 0 && corners[corner + stride] !== 0 && corners[corner + stride + 1] !== 0) {
        states[at] = COVERED;
      }
    }
  }
}

/**
 * Marks the showing cells a tile layer holds a tile in as under a tile, the estimate.
 *
 * @param states The states, changed in place.
 * @param drawn The tile layer's cells.
 */
function underTiles(states: Uint8Array, drawn: Uint32Array): void {
  for (let at = 0; at < states.length; at += 1) {
    if (states[at] === SHOWING && drawn[at] !== EMPTY_TILE) {
      states[at] = UNDER;
    }
  }
}

/**
 * Marks the showing cells a prop's frame box holds whole as under a prop, the estimate.
 *
 * @param states The states, changed in place.
 * @param prop The prop.
 * @param sources The frame sizes and the tile size.
 * @param width The map's width in cells.
 * @param height The map's height in cells.
 */
function underPropBox(states: Uint8Array, prop: PropInstance, sources: HighlightSources, width: number, height: number): void {
  const size = sources.frameSize(prop.frame);
  if (size === undefined) {
    return;
  }
  const tile = sources.tileSize;
  const left = prop.x * tile;
  const bottom = (prop.y + 1) * tile;
  const x0 = Math.max(0, Math.ceil(left / tile));
  const x1 = Math.min(width, Math.floor((left + size.w) / tile));
  const y0 = Math.max(0, Math.ceil((bottom - size.h) / tile));
  const y1 = Math.min(height, Math.floor(bottom / tile));
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      if (states[y * width + x] === SHOWING) {
        states[y * width + x] = UNDER;
      }
    }
  }
}

/**
 * Whether a mask is solid over a whole tile: at least a tile each way and no clear pixel.
 *
 * @param mask The frame's solid pixels.
 * @param tileSize Tile size in pixels.
 * @returns True when a cell under the frame shows nothing.
 */
function solidTile(mask: FrameMask, tileSize: number): boolean {
  return mask.w >= tileSize && mask.h >= tileSize && mask.solid.every((pixel) => pixel === 1);
}

/**
 * A reading of frames' masks that asks the host once per frame id and takes a frame the atlas does not hold
 * (`MissingFrameError`) as holding no solid pixel, so one absent frame covers nothing. Any other failure means
 * the atlas cannot be read and is thrown on: nothing can be measured then.
 *
 * @param maskOf The host's lookup.
 * @returns The lookup, remembered; null for a frame the atlas lacks.
 * @throws Whatever the host's lookup throws for an atlas it cannot read.
 */
function remembered(maskOf: FrameMaskOf): (frame: string) => FrameMask | null {
  const known = new Map<string, FrameMask | null>();
  return (frame) => {
    const had = known.get(frame);
    if (had !== undefined) {
      return had;
    }
    let mask: FrameMask | null;
    try {
      mask = maskOf(frame);
    } catch (error) {
      if (!(error instanceof MissingFrameError)) {
        throw error;
      }
      mask = null;
    }
    known.set(frame, mask);
    return mask;
  };
}

/**
 * Marks the showing cells a tile or terrain layer covers by measurement: those whose tile's frame is solid
 * over the whole tile, whatever its mirroring.
 *
 * @param states The states, changed in place.
 * @param drawn The layer's cells.
 * @param variant The layer's image variant, null for the default.
 * @param sources The tileset ids and the tile size.
 * @param maskOf The remembered mask lookup.
 */
function coverBySolidTiles(states: Uint8Array, drawn: Uint32Array, variant: string | null, sources: HighlightSources, maskOf: (frame: string) => FrameMask | null): void {
  const solid = new Map<number, boolean>();
  for (let at = 0; at < states.length; at += 1) {
    const packed = drawn[at] as number;
    if (states[at] !== SHOWING || packed === EMPTY_TILE) {
      continue;
    }
    let covers = solid.get(packed);
    if (covers === undefined) {
      const { tilesetIndex, tile } = unpackTile(packed);
      const tileset = sources.tilesets[tilesetIndex];
      const mask = tileset === undefined ? null : maskOf(frameId(tileset, variant, tile));
      covers = mask !== null && solidTile(mask, sources.tileSize);
      solid.set(packed, covers);
    }
    if (covers) {
      states[at] = COVERED;
    }
  }
}

/**
 * Whether a prop's solid pixels fill a whole cell, read through the prop's flips as `propCoverage` reads them,
 * stopping at the first clear pixel.
 *
 * @param prop The prop.
 * @param mask Its frame's solid pixels.
 * @param tileSize Tile size in pixels.
 * @param cx The cell's column.
 * @param cy The cell's row.
 * @returns True when no pixel of the cell lies off the prop's solid pixels.
 */
function propFillsCell(prop: PropInstance, mask: FrameMask, tileSize: number, cx: number, cy: number): boolean {
  const left = prop.x * tileSize;
  const top = (prop.y + 1) * tileSize - mask.h;
  const x0 = cx * tileSize;
  const y0 = cy * tileSize;
  if (x0 < Math.ceil(left) || x0 + tileSize > Math.ceil(left + mask.w) || y0 < Math.ceil(top) || y0 + tileSize > Math.ceil(top + mask.h)) {
    return false;
  }
  for (let y = y0; y < y0 + tileSize; y += 1) {
    const dy = Math.floor(y - top);
    const row = (prop.flipY ? mask.h - 1 - dy : dy) * mask.w;
    for (let x = x0; x < x0 + tileSize; x += 1) {
      const dx = Math.floor(x - left);
      if (mask.solid[row + (prop.flipX ? mask.w - 1 - dx : dx)] !== 1) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Marks the showing cells a prop covers by measurement: those its solid pixels fill whole.
 *
 * @param states The states, changed in place.
 * @param prop The prop.
 * @param sources The tile size.
 * @param maskOf The remembered mask lookup.
 * @param width The map's width in cells.
 * @param height The map's height in cells.
 */
function coverBySolidProp(states: Uint8Array, prop: PropInstance, sources: HighlightSources, maskOf: (frame: string) => FrameMask | null, width: number, height: number): void {
  const mask = maskOf(frameId(prop.frame.tileset, null, prop.frame.tile));
  if (mask === null) {
    return;
  }
  const tile = sources.tileSize;
  const left = prop.x * tile;
  const top = (prop.y + 1) * tile - mask.h;
  const x1 = Math.min(width - 1, Math.floor((left + mask.w - 1) / tile));
  const y1 = Math.min(height - 1, Math.floor((top + mask.h - 1) / tile));
  for (let cy = Math.max(0, Math.floor(top / tile)); cy <= y1; cy += 1) {
    for (let cx = Math.max(0, Math.floor(left / tile)); cx <= x1; cx += 1) {
      if (states[cy * width + cx] === SHOWING && propFillsCell(prop, mask, tile, cx, cy)) {
        states[cy * width + cx] = COVERED;
      }
    }
  }
}

/**
 * The frame ids a map's highlights may ask the atlas for: every tile a tile or terrain layer draws, in the
 * layer's variant and whatever its mirroring, and every prop's frame, each once. A host reads their masks ahead
 * in idle moments, so the first measured highlight of a session finds them and does not wait on the atlas.
 *
 * @param map The map.
 * @param sources The cells the renderer draws and the world's tileset ids.
 * @returns The frame ids, in the order first met from the bottom layer up.
 */
export function highlightFrames(map: MapDocument, sources: Pick<HighlightSources, "cells" | "tilesets">): string[] {
  const frames = new Set<string>();
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        frames.add(frameId(prop.frame.tileset, null, prop.frame.tile));
      }
      continue;
    }
    const drawn = layer.kind === "tile" || layer.kind === "terrain" ? sources.cells(layer.id) : null;
    if (drawn === null) {
      continue;
    }
    const variant = layer.kind === "terrain" ? layer.variant : null;
    const seen = new Set<number>();
    for (let at = 0; at < drawn.length; at += 1) {
      const packed = drawn[at] as number;
      if (packed === EMPTY_TILE || seen.has(packed)) {
        continue;
      }
      seen.add(packed);
      const { tilesetIndex, tile } = unpackTile(packed);
      const tileset = sources.tilesets[tilesetIndex];
      if (tileset !== undefined) {
        frames.add(frameId(tileset, variant, tile));
      }
    }
  }
  return [...frames];
}

/**
 * The states of a tile or terrain layer's cells: 0 where it draws nothing, else showing, covered or under.
 *
 * @param map The map.
 * @param index The layer's index.
 * @param sources The sources.
 * @returns One state per cell of the map.
 * @throws Whatever the mask lookup throws for an atlas it cannot read.
 */
function judgedStates(map: MapDocument, index: number, sources: HighlightSources): Uint8Array {
  const { width, height, layers } = map;
  const states = new Uint8Array(width * height);
  const own = sources.cells((layers[index] as LayerDocument).id);
  if (own === null) {
    return states;
  }
  for (let at = 0; at < states.length; at += 1) {
    states[at] = own[at] === EMPTY_TILE ? 0 : SHOWING;
  }
  const maskOf = sources.maskOf === undefined ? null : remembered(sources.maskOf);
  layers.forEach((layer, above) => {
    if (!drawsOver(layers, sources.playersAbove, index, above)) {
      return;
    }
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        if (maskOf === null) {
          underPropBox(states, prop, sources, width, height);
        } else {
          coverBySolidProp(states, prop, sources, maskOf, width, height);
        }
      }
      return;
    }
    if (layer.kind !== "terrain" && layer.kind !== "tile") {
      return;
    }
    const drawn = sources.cells(layer.id);
    if (drawn === null) {
      return;
    }
    if (maskOf !== null) {
      coverBySolidTiles(states, drawn, layer.kind === "terrain" ? layer.variant : null, sources, maskOf);
    } else if (layer.kind === "terrain") {
      coverByCorners(states, layer, drawn, width, height);
    } else {
      underTiles(states, drawn);
    }
  });
  return states;
}

/**
 * The cells a prop or marker layer's things stand on, on the tile grid: a prop's frame box (`propBox`), a
 * marker's cell, clipped to the map.
 *
 * @param map The map.
 * @param layer The layer.
 * @param sources The frame sizes and the tile size.
 * @returns One state per cell: showing where a thing stands.
 */
function thingStates(map: MapDocument, layer: Extract<LayerDocument, { kind: "prop" | "entity" }>, sources: HighlightSources): Uint8Array {
  const { width, height } = map;
  const tile = sources.tileSize;
  const states = new Uint8Array(width * height);
  const mark = (x0: number, y0: number, x1: number, y1: number): void => {
    for (let y = Math.max(0, y0); y < Math.min(height, y1); y += 1) {
      states.fill(SHOWING, y * width + Math.max(0, x0), y * width + Math.max(0, Math.min(width, x1)));
    }
  };
  if (layer.kind === "prop") {
    for (const prop of layer.props) {
      const box = propBox(prop, sources.frameSize, tile);
      mark(Math.floor(box.x / tile), Math.floor(box.y / tile), Math.ceil((box.x + box.w) / tile), Math.ceil((box.y + box.h) / tile));
    }
  } else {
    for (const entity of layer.entities) {
      mark(Math.floor(entity.x), Math.floor(entity.y), Math.floor(entity.x) + 1, Math.floor(entity.y) + 1);
    }
  }
  return states;
}

/**
 * Cuts a grid of states into the highlight's three bitmaps, its cell counts and its bounding box.
 *
 * @param states One state per cell.
 * @param width The grid's width in cells.
 * @param height The grid's height in cells.
 * @param cellPixels A cell's size in world pixels.
 * @returns The bitmaps, the counts by state and the bounds.
 */
function cut(states: Uint8Array, width: number, height: number, cellPixels: number): Pick<LayerHighlight, "showing" | "covered" | "under" | "bounds"> & { readonly counts: HighlightCounts } {
  const bitmaps = [new Uint8Array(0), new Uint8Array(states.length), new Uint8Array(states.length), new Uint8Array(states.length)];
  const byState = [0, 0, 0, 0];
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const state = states[y * width + x] as number;
      if (state === 0) {
        continue;
      }
      byState[state] = (byState[state] as number) + 1;
      (bitmaps[state] as Uint8Array)[y * width + x] = 1;
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
  }
  const [, shown = 0, hidden = 0, under = 0] = byState;
  const area = (cells: Uint8Array | undefined): PresenceArea => ({ x0: 0, y0: 0, width, height, cells: cells as Uint8Array });
  return {
    showing: area(bitmaps[SHOWING]),
    covered: area(bitmaps[COVERED]),
    under: area(bitmaps[UNDER]),
    counts: { total: shown + hidden + under, showing: shown, covered: hidden, under },
    bounds: x1 < 0 ? null : { x: x0 * cellPixels, y: y0 * cellPixels, w: (x1 - x0 + 1) * cellPixels, h: (y1 - y0 + 1) * cellPixels },
  };
}

/**
 * The highlight of one layer of a map.
 *
 * @param map The map.
 * @param layerId The layer.
 * @param sources The cells the renderer draws, the frame sizes, the tile size, the tilesets, the Players band
 *   and, when the host has them, the frames' solid pixels.
 * @returns The highlight, or null when the map holds no such layer. When the frames' pixels are given and
 *   cannot be read, the highlight is the estimate and says so (`measured` false).
 * @example
 * const highlight = layerHighlight(store.map, layerId, sources);
 * const words = highlight === null ? "" : `${highlight.counts.total} cells, ${highlight.counts.showing} showing`;
 */
export function layerHighlight(map: MapDocument, layerId: string, sources: HighlightSources): LayerHighlight | null {
  const index = map.layers.findIndex((layer) => layer.id === layerId);
  const layer = map.layers[index];
  if (layer === undefined) {
    return null;
  }
  const head = { layerId, kind: layer.kind, empty: layerIsEmpty(layer), measured: sources.maskOf !== undefined };
  if (layer.kind === "tile" || layer.kind === "terrain") {
    let states: Uint8Array;
    let measured = head.measured;
    try {
      states = judgedStates(map, index, sources);
    } catch {
      measured = false;
      states = judgedStates(map, index, { ...sources, maskOf: undefined });
    }
    return { ...head, measured, judged: true, cellPixels: sources.tileSize, ...cut(states, map.width, map.height, sources.tileSize) };
  }
  if (layer.kind === "prop" || layer.kind === "entity") {
    const parts = cut(thingStates(map, layer, sources), map.width, map.height, sources.tileSize);
    const things = layer.kind === "prop" ? layer.props.length : layer.entities.length;
    return { ...head, judged: false, cellPixels: sources.tileSize, ...parts, counts: { total: things, showing: things, covered: 0, under: 0 } };
  }
  const n = COLLISION_CELLS_PER_TILE;
  const states = Uint8Array.from(layer.cells, (cell) => (cell === 0 ? 0 : SHOWING));
  return { ...head, judged: false, cellPixels: sources.tileSize / n, ...cut(states, map.width * n, map.height * n, sources.tileSize / n) };
}
